import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { STORE_PATH_FUNCTION_EXECUTION, STORE_PREFIX } from '../../constants';
import {
	PageStoreExtractor,
	addListener,
	getDataFromPath,
	getPathFromLocation,
	setData,
	UrlDetailsExtractor,
} from '../../context/StoreContext';
import { Component, ComponentPropertyDefinition, ComponentProps } from '../../types/common';
import { processComponentStylePseudoClasses } from '../../util/styleProcessor';
import Children from '../Children';
import { HelperComponent } from '../HelperComponents/HelperComponent';
import { getHref } from '../util/getHref';
import { onMouseDownDragStartCurry } from '../../functions/utils';
import { runEvent } from '../util/runEvent';
import useDefinition from '../util/useDefinition';
import { flattenUUID } from '../util/uuid';
import { personalizationEvent } from '../util/personalization';
import GridStyle from './GridStyle';
import { propertiesDefinition, stylePropertiesDefinition } from './gridProperties';
import { isNullValue } from '@fincity/kirun-js';
import { styleProperties, styleDefaults } from './gridStyleProperties';
import { IconHelper } from '../util/IconHelper';

function Grid(props: Readonly<ComponentProps>) {
	const location = useLocation();
	const [hover, setHover] = React.useState(false);
	const [focus, setFocus] = React.useState(false);
	const ref = React.useRef(null);
	const { definition, pageDefinition, locationHistory, context } = props;
	const {
		definition: { bindingPath, bindingPath2, bindingPath3, bindingPath4 },
	} = props;
	const pageExtractor = PageStoreExtractor.getForContext(context.pageName);
	const urlExtractor = UrlDetailsExtractor.getForContext(context.pageName);
	const {
		key,
		stylePropertiesWithPseudoStates,
		properties: {
			readOnly: isReadonly = false,
			linkPath,
			target,
			containerType,
			layout,
			onClick,
			background,
			onMouseEnter,
			onMouseLeave,
			onLeavingViewport,
			onEnteringViewport,
			dragData,
			border,
			borderRadius,
			boxShadow,
			stopPropagation,
			preventDefault,
			dragDataPrefix,
			dragDataType,
			dropDataPrefix,
			dropDataType,
			onDropData,
			padding,
			resizeHandle = '_NONE',
			resizeMin = 80,
			resizeMax = 2000,
			enablePersonalization = true,
		} = {},
	} = useDefinition(
		definition,
		propertiesDefinition,
		stylePropertiesDefinition,
		locationHistory,
		pageExtractor,
		urlExtractor,
	);

	const childs = (
		<Children
			key={`${key}_chld`}
			pageDefinition={pageDefinition}
			renderableChildren={definition.children}
			context={{ ...context, isReadonly }}
			locationHistory={locationHistory}
		/>
	);

	const dragstartHandler = (ev: React.DragEvent<HTMLElement>) => {
		let data = dragData;
		if (dragDataType === 'application/json')
			data = dragData === undefined ? 'undefined' : JSON.stringify(dragData);
		if (dragDataPrefix) data = dragDataPrefix + data;
		ev.dataTransfer.setData(dragDataType, data);
	};

	const resolvedStyles = processComponentStylePseudoClasses(
		props.pageDefinition,
		{ focus, hover, disabled: isReadonly },
		stylePropertiesWithPseudoStates,
	);

	const clickEvent = onClick ? props.pageDefinition.eventFunctions?.[onClick] : undefined;
	const onMouseEnterEvent = onMouseEnter
		? props.pageDefinition.eventFunctions?.[onMouseEnter]
		: undefined;
	const onMouseLeaveEvent = onMouseLeave
		? props.pageDefinition.eventFunctions?.[onMouseLeave]
		: undefined;
	const onEnteringViewportEvent = onEnteringViewport
		? props.pageDefinition.eventFunctions?.[onEnteringViewport]
		: undefined;
	const onLeavingViewportEvent = onLeavingViewport
		? props.pageDefinition.eventFunctions?.[onLeavingViewport]
		: undefined;

	const bindingPathPath = bindingPath
		? getPathFromLocation(bindingPath, locationHistory, pageExtractor)
		: undefined;

	const bindingPathPath2 = bindingPath2
		? getPathFromLocation(bindingPath2, locationHistory, pageExtractor)
		: undefined;

	const bindingPathPath3 = bindingPath3
		? getPathFromLocation(bindingPath3, locationHistory, pageExtractor)
		: undefined;

	// ── Resizable pane ──────────────────────────────────────────────────────
	// The dragged size is remembered the way Table remembers its column layout:
	// one personalization document per component key. Gated on resizeHandle as
	// well as the flag, because a Grid with no grip has nothing to remember and
	// there are dozens of Grids on a page.
	const isResizable = resizeHandle !== '_NONE';
	const isVerticalResize = resizeHandle === '_TOP' || resizeHandle === '_BOTTOM';

	const personalizationBindingPath =
		isResizable && enablePersonalization
			? ((bindingPath4 &&
					getPathFromLocation(bindingPath4, locationHistory, pageExtractor)) ??
				`${STORE_PREFIX}.personalization.${context.pageName}.${flattenUUID(key)}`)
			: undefined;

	const [resizeSize, setResizeSize] = useState<number | undefined>(() =>
		personalizationBindingPath
			? getDataFromPath(`${personalizationBindingPath}.resizeSize`, locationHistory)
			: undefined,
	);

	useEffect(
		() =>
			personalizationEvent({
				prefix: 'grid',
				personalizationBindingPath,
				key,
				locationHistory,
				pageExtractor,
				onLoad: data => setResizeSize(data.resizeSize),
			}),
		[personalizationBindingPath],
	);

	// Built on onMouseDownDragStartCurry rather than hand-rolled listeners: it bails
	// when the button is no longer held, tears down on mouseleave, and puts a
	// shield over the viewport so an iframe in the layout cannot swallow the drag.
	const onResizeStart = (e: React.MouseEvent) => {
		const el = ref.current as HTMLElement | null;
		if (!el) return;
		// The same test the curry makes, made here too: everything below this line
		// is undone by the drag ending, and a curry that bails never starts one.
		if (e.buttons !== 1) return;

		const startSize = isVerticalResize ? el.offsetHeight : el.offsetWidth;
		// A grip on the left or top edge grows the box when dragged towards the
		// origin, so the delta runs the other way.
		const direction = resizeHandle === '_LEFT' || resizeHandle === '_TOP' ? -1 : 1;
		const dimension = isVerticalResize ? 'height' : 'width';

		const previousCursor = document.body.style.cursor;
		const previousSelect = document.body.style.userSelect;
		document.body.style.cursor = isVerticalResize ? 'row-resize' : 'col-resize';
		document.body.style.userSelect = 'none';

		const sizeFrom = (diffX: number, diffY: number) =>
			Math.max(
				resizeMin,
				Math.min(resizeMax, startSize + (isVerticalResize ? diffY : diffX) * direction),
			);

		let size = startSize;

		onMouseDownDragStartCurry(
			0,
			0,
			(_x, _y, diffX, diffY) => {
				size = sizeFrom(diffX, diffY);
				// Straight to the DOM while dragging. A store write here would
				// re-evaluate every expression on the page on every mouse move.
				el.style[dimension] = `${size}px`;
			},
			(_x, _y, diffX, diffY) => {
				size = sizeFrom(diffX, diffY);
				document.body.style.cursor = previousCursor;
				document.body.style.userSelect = previousSelect;

				setResizeSize(size);
				if (personalizationBindingPath) {
					const current =
						getDataFromPath(
							personalizationBindingPath,
							locationHistory,
							pageExtractor,
						) ?? {};
					// The personalization listener picks this up and POSTs it, debounced.
					setData(
						personalizationBindingPath,
						{ ...current, resizeSize: size },
						context.pageName,
					);
				}
			},
		)(e);
	};

	const resizeStyle =
		isResizable && resizeSize
			? { [isVerticalResize ? 'height' : 'width']: `${resizeSize}px` }
			: undefined;

	const resizeGrip = isResizable ? (
		<div
			key={`${key}_resize`}
			className={`_gridResizeHandle ${resizeHandle}`}
			onMouseDown={onResizeStart}
			role="separator"
			aria-orientation={isVerticalResize ? 'horizontal' : 'vertical'}
		/>
	) : undefined;

	const spinnerPath = `${STORE_PATH_FUNCTION_EXECUTION}.${props.context.pageName}.${flattenUUID(
		key,
	)}.isRunning`;

	const [isLoading, setIsLoading] = useState(
		getDataFromPath(spinnerPath, locationHistory, pageExtractor) ?? false,
	);

	useEffect(
		() => addListener(props.context.pageName, (_, value) => setIsLoading(value), spinnerPath),
		[],
	);
	const handleClick =
		!clickEvent || isLoading
			? undefined
			: () =>
					(async () =>
						await runEvent(
							clickEvent,
							onClick,
							props.context.pageName,
							props.locationHistory,
							props.pageDefinition,
						))();

	const sepStyle = resolvedStyles?.comp?.hideScrollBar;
	const styleComp = sepStyle ? (
		<style
			key={`${key}_style`}
		>{`._${key}_grid_css::-webkit-scrollbar { display: none }`}</style>
	) : undefined;

	useEffect(() => {
		if (
			!ref.current ||
			(isNullValue(onEnteringViewportEvent) && isNullValue(onLeavingViewportEvent))
		)
			return;

		const thresholds = [];
		if (onEnteringViewport) thresholds.push(0.1);
		if (onLeavingViewport) thresholds.push(0.9);

		let firstTime = true;

		const observer = new IntersectionObserver(
			(entries, observer) => {
				if (firstTime) {
					firstTime = false;
					return;
				}

				if (
					onEnteringViewport &&
					Math.abs(Math.round(entries[0].intersectionRatio * 100) - 10) < 3
				) {
					(async () =>
						await runEvent(
							onEnteringViewportEvent,
							onEnteringViewport,
							props.context.pageName,
							props.locationHistory,
							props.pageDefinition,
						))();
				} else if (
					onLeavingViewport &&
					Math.abs(Math.round(entries[0].intersectionRatio * 100) - 90) < 3
				) {
					(async () =>
						await runEvent(
							onLeavingViewportEvent,
							onLeavingViewport,
							props.context.pageName,
							props.locationHistory,
							props.pageDefinition,
						))();
				}
			},
			{ root: null, rootMargin: '0px', threshold: thresholds },
		);

		observer.observe(ref.current);
		return () => observer.disconnect();
	}, [ref.current, onEnteringViewport, onLeavingViewport]);

	let onDragOverFunction: React.DragEventHandler<HTMLInputElement> | undefined = undefined;
	let onDropFunction: React.DragEventHandler<HTMLInputElement> | undefined = undefined;

	if (bindingPathPath3 || onDropData) {
		onDragOverFunction = e => e.preventDefault();

		onDropFunction = e => {
			e.preventDefault();
			let data = e.dataTransfer.getData(dropDataType);
			if (dropDataPrefix) {
				if (!data.startsWith(dropDataPrefix)) return;
				data = data.substring(dropDataPrefix.length);
			}
			if (dropDataType === 'application/json') data = JSON.parse(data);
			if (bindingPathPath3) setData(bindingPathPath3, data, context.pageName);
			if (!onDropData || !pageDefinition.eventFunctions[onDropData]) return;
			(async () =>
				await runEvent(
					pageDefinition.eventFunctions[onDropData],
					onDropData,
					props.context.pageName,
					props.locationHistory,
					props.pageDefinition,
				))();
		};
	}

	if (linkPath) {
		return React.createElement(
			containerType.toLowerCase(),
			{
				className: 'comp compGrid',
				id: key,
				draggable: !!dragData,
				onDragStart: dragData ? dragstartHandler : undefined,
				onDragOver: onDragOverFunction,
				onDrop: onDropFunction,
			},
			[
				<HelperComponent
					context={props.context}
					key={`${key}_hlp`}
					definition={definition}
				/>,
				styleComp,
				<Link
					key={`${key}_Link`}
					ref={ref}
					className={`_anchorGrid _${layout} ${background} ${border} ${borderRadius} ${boxShadow} ${padding} ${
						sepStyle ? `_${key}_grid_css` : ''
					}`}
					onMouseEnter={
						stylePropertiesWithPseudoStates?.hover || onMouseEnterEvent
							? () => {
									setHover(true);

									if (!onMouseEnterEvent) return;
									(async () =>
										await runEvent(
											onMouseEnterEvent,
											onMouseEnter,
											props.context.pageName,
											props.locationHistory,
											props.pageDefinition,
										))();
								}
							: undefined
					}
					onMouseLeave={
						stylePropertiesWithPseudoStates?.hover || onMouseLeaveEvent
							? () => {
									setHover(false);
									if (!onMouseLeaveEvent) return;
									(async () =>
										await runEvent(
											onMouseLeaveEvent,
											onMouseLeave,
											props.context.pageName,
											props.locationHistory,
											props.pageDefinition,
										))();
								}
							: undefined
					}
					onMouseDown={handleClick}
					onFocus={
						stylePropertiesWithPseudoStates?.focus ? () => setFocus(true) : undefined
					}
					onBlur={
						stylePropertiesWithPseudoStates?.focus ? () => setFocus(false) : undefined
					}
					to={getHref(linkPath, location) ?? ''}
					target={target}
					style={resolvedStyles.comp ?? {}}
				>
					{childs}
				</Link>,
			],
		);
	}

	const onScrollFunction =
		bindingPathPath || bindingPathPath2
			? (e: any) => {
					if (bindingPathPath) {
						const w = e.target.scrollWidth - e.target.clientWidth;
						setData(
							bindingPathPath,
							100 - Math.round(((w - e.target.scrollLeft) * 100) / w),
							context.pageName,
						);
					}
					if (bindingPathPath2) {
						const h = e.target.scrollHeight - e.target.clientHeight;
						setData(
							bindingPathPath2,
							100 - Math.round(((h - e.target.scrollTop) * 100) / h),
							context.pageName,
						);
					}
				}
			: undefined;

	return React.createElement(
		containerType.toLowerCase(),
		{
			onScroll: onScrollFunction,
			onMouseEnter:
				stylePropertiesWithPseudoStates?.hover || onMouseEnterEvent
					? () => {
							setHover(true);
							if (!onMouseEnterEvent) return;
							(async () =>
								await runEvent(
									onMouseEnterEvent,
									onMouseEnter,
									props.context.pageName,
									props.locationHistory,
									props.pageDefinition,
								))();
						}
					: undefined,

			onMouseLeave:
				stylePropertiesWithPseudoStates?.hover || onMouseLeaveEvent
					? () => {
							setHover(false);
							if (!onMouseLeaveEvent) return;
							(async () =>
								await runEvent(
									onMouseLeaveEvent,
									onMouseLeave,
									props.context.pageName,
									props.locationHistory,
									props.pageDefinition,
								))();
						}
					: undefined,
			onDragStart: !!dragData ? dragstartHandler : undefined,
			onDragOver: onDragOverFunction,
			onDrop: onDropFunction,
			onFocus: stylePropertiesWithPseudoStates?.focus ? () => setFocus(true) : undefined,
			onBlur: stylePropertiesWithPseudoStates?.focus ? () => setFocus(false) : undefined,
			ref: ref,
			draggable: !!dragData,
			className: `comp compGrid _noAnchorGrid _${layout} ${background} ${border} ${borderRadius} ${boxShadow} ${padding} ${
				sepStyle ? `_${key}_grid_css` : ''
			}${isResizable ? ' _resizableGrid' : ''}`,
			style: { ...(resolvedStyles.comp ?? {}), ...resizeStyle },

			onClick: ev => {
				if (stopPropagation) ev.stopPropagation();
				if (preventDefault) ev.preventDefault();
				handleClick?.();
			},
			id: key,
		},
		[
			<HelperComponent context={props.context} key={`${key}_hlp`} definition={definition} />,
			styleComp,
			resizeGrip,
			childs,
		],
	);
}

const component: Component = {
	order: 1,
	name: 'Grid',
	displayName: 'Grid',
	description: 'Grid component',
	component: Grid,
	propertyValidation: (props: ComponentPropertyDefinition): Array<string> => [],
	properties: propertiesDefinition,
	styleComponent: GridStyle,
	styleDefaults: styleDefaults,
	stylePseudoStates: ['hover', 'focus', 'readonly'],
	allowedChildrenType: new Map<string, number>([['', -1]]),
	styleProperties: stylePropertiesDefinition,
	bindingPaths: {
		bindingPath: { name: 'Grid X Scroll Percentage Binding' },
		bindingPath2: { name: 'Grid Y Scroll Percentage Binding' },
		bindingPath3: { name: 'Dropped Data Binding' },
		bindingPath4: { name: 'Personalization Binding' },
	},
	defaultTemplate: {
		key: '',
		name: 'Grid',
		type: 'Grid',
	},
	sections: [
		{
			name: 'Grid',
			pageName: 'grid',
		},
	],
	stylePropertiesForTheme: styleProperties,
};

export default component;
